import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PRICING_TIERS, PAYMENT_METHODS } from '../data/mock';
import { CalendlyEmbed } from '../components/CalendlyEmbed';
import { Badge } from '../components/ui/badge';
import * as Icons from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

export default function PricingPage() {
  return (
    <main>
      {/* Hero */}
      <section className="pt-32 pb-16 px-6" style={{ background: 'radial-gradient(at 53% 78%, hsla(60,100%,50%,0.15) 0px, transparent 50%), radial-gradient(at 71% 91%, hsla(108,100%,50%,0.15) 0px, transparent 50%)' }}>
        <div className="max-w-[800px] mx-auto text-center">
          <motion.div {...fadeInUp}>
            <Badge className="mb-5 bg-[rgba(148,242,127,0.12)] text-[rgb(13,121,22)] border-[rgba(148,242,127,0.3)] px-4 py-1.5 text-xs font-medium rounded-full">
              Transparent Pricing
            </Badge>
          </motion.div>
          <motion.h1 {...fadeInUp} transition={{ delay: 0.1 }}
            className="font-bold text-[rgb(0,55,32)] tracking-tight mb-4"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)', lineHeight: '0.92' }}>
            Simple, Clear Pricing
          </motion.h1>
          <motion.p {...fadeInUp} transition={{ delay: 0.2 }}
            className="text-[rgb(131,146,140)] max-w-[600px] mx-auto" style={{ fontSize: 'clamp(1rem, 2vw, 1.15rem)' }}>
            We believe in upfront pricing. You'll always know the cost before we begin. No hidden fees.
          </motion.p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PRICING_TIERS.map((tier, i) => (
              <motion.div key={tier.title} {...fadeInUp} transition={{ delay: i * 0.08 }}
                className={`rounded-xl p-7 border transition-all duration-200 hover:shadow-lg ${
                  tier.highlight ? 'border-[#8FEC78] bg-[rgba(148,242,127,0.03)] shadow-md' : 'border-black/10 bg-white'
                }`}>
                {tier.highlight && (
                  <Badge className="mb-3 bg-[rgba(148,242,127,0.15)] text-[rgb(13,121,22)] border-transparent text-xs rounded-full">Most Popular</Badge>
                )}
                <h3 className="font-semibold text-[rgb(0,55,32)] text-xl mb-1">{tier.title}</h3>
                <div className="flex items-baseline gap-1.5 mb-3">
                  {tier.prefix && <span className="text-sm text-[rgb(131,146,140)]">{tier.prefix}</span>}
                  <span className="text-3xl font-bold text-[rgb(0,55,32)]">{tier.price}</span>
                </div>
                <p className="text-sm text-[rgb(131,146,140)] mb-5 leading-relaxed">{tier.description}</p>
                <ul className="space-y-2.5 mb-6">
                  {tier.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-[rgb(14,15,12)]">
                      <Icons.Check className="w-4 h-4 text-[#8FEC78] mt-0.5 shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/book" className="inline-flex items-center justify-center w-full gap-2 px-5 py-3 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] text-white font-semibold text-sm no-underline hover:shadow-md transition-shadow">
                  {tier.cta} <Icons.ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Pricing Notes */}
          <motion.div {...fadeInUp} className="mt-12 bg-[rgba(0,0,0,0.02)] rounded-xl border border-black/10 p-6">
            <h3 className="font-semibold text-[rgb(0,55,32)] text-base mb-4">Pricing Notes</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Icons.Info className="w-4 h-4 text-[rgb(13,121,22)] mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[rgb(0,55,32)] mb-1">What "Starting at" Means</p>
                  <p className="text-xs text-[rgb(131,146,140)] leading-relaxed">Base pricing covers standard returns. Complex situations like multiple states, significant business activity, or unusual circumstances may adjust the final price. We always confirm before starting.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Icons.Clock className="w-4 h-4 text-[rgb(13,121,22)] mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[rgb(0,55,32)] mb-1">2-Day Turnaround</p>
                  <p className="text-xs text-[rgb(131,146,140)] leading-relaxed">Standard turnaround is 2 business days after all required documents are received. Need it faster? Add Expedited Processing for $99.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Icons.CreditCard className="w-4 h-4 text-[rgb(13,121,22)] mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[rgb(0,55,32)] mb-1">When You Pay</p>
                  <p className="text-xs text-[rgb(131,146,140)] leading-relaxed">We send an invoice after reviewing your documents and preparing your return. Payment is due before filing.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Icons.Wallet className="w-4 h-4 text-[rgb(13,121,22)] mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[rgb(0,55,32)] mb-1">Payment Methods</p>
                  <p className="text-xs text-[rgb(131,146,140)] leading-relaxed">
                    {PAYMENT_METHODS.join(' · ')} — and other options available on request.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA + Calendly */}
      <section className="py-20" style={{ background: 'rgba(0,0,0,0.02)' }}>
        <div className="max-w-[900px] mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-10">
            <h2 className="font-semibold text-[rgb(0,55,32)] tracking-tight mb-3" style={{ fontSize: 'clamp(1.875rem, 4vw, 2.5rem)' }}>Have Questions About Pricing?</h2>
            <p className="text-[rgb(131,146,140)]">Book a free consultation. We'll review your situation and give you a clear quote—no obligation.</p>
          </motion.div>
          <motion.div {...fadeInUp} className="rounded-2xl border border-black/10 overflow-hidden bg-white shadow-sm">
            <CalendlyEmbed minimal />
          </motion.div>
        </div>
      </section>
    </main>
  );
}
